import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { brand, nav, site } from '../../data/site';
import { categories } from '../../data/collections';
import { useEnquiry } from '../../context/EnquiryContext';
import { IconClose, IconInstagram, IconFacebook, Lotus } from '../ui/Icons';

/* Minimal bar: mark + wordmark, two quiet links, and a Menu trigger that
   opens a full-screen index. No utility strip, no filled buttons. */
export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openEnquiry } = useEnquiry();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      {/* Always-white masthead — the navy mark stays legible over every
          section, dark or light. Only the shadow reacts to scroll. */}
      <div className="fixed inset-x-0 top-0 z-[80]">
        <header
          className={`border-b bg-white transition-shadow duration-500 ease-luxe ${
            scrolled ? 'border-royal/10 shadow-[0_1px_24px_-12px_rgba(2,25,69,0.35)]' : 'border-royal/10'
          }`}
        >
          <div className="shell flex h-[76px] items-center justify-between gap-6 lg:h-[96px]">
            {/* Mark */}
            <Link to="/" className="flex shrink-0 items-center gap-3.5" aria-label="Pramukh Jewellers — home">
              <img src={brand.markBlue} alt="" className="h-8 w-auto lg:h-10" />
              <span className="hidden sm:block">
                <span className="block font-display text-[17px] leading-none tracking-[0.22em] text-royal lg:text-[20px]">
                  PRAMUKH
                </span>
                <span className="mt-[5px] block text-[10px] uppercase leading-none tracking-luxe text-gold-600 lg:text-[10.5px]">
                  Jewellers · Est. 1997
                </span>
              </span>
            </Link>

            {/* Quiet links + menu */}
            <div className="flex items-center gap-7 lg:gap-10">
              <NavLink
                to="/collections"
                className="hidden text-[12px] font-medium uppercase tracking-luxe text-royal/75 transition-colors duration-500 hover:text-gold-600 sm:block"
              >
                Collections
              </NavLink>

              <button
                type="button"
                onClick={() => openEnquiry('', 'manual')}
                className="hidden text-[12px] font-medium uppercase tracking-luxe text-royal/75 transition-colors duration-500 hover:text-gold-600 sm:block"
              >
                Enquire
              </button>

              <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="Open menu"
                aria-expanded={open}
                className="group flex items-center gap-3 text-[12px] font-medium uppercase tracking-luxe text-royal transition-colors duration-500 hover:text-gold-600"
              >
                <span>Menu</span>
                <span className="flex h-4 w-6 flex-col justify-center gap-[5px]">
                  <span className="block h-px w-full bg-current transition-transform duration-500 ease-luxe group-hover:translate-x-1" />
                  <span className="block h-px w-full bg-current transition-transform duration-500 ease-luxe group-hover:-translate-x-1" />
                </span>
              </button>
            </div>
          </div>
        </header>
      </div>

      {/* ---- Slide-in index (right-hand panel) ---- */}
      <div
        className={`fixed inset-0 z-[95] ${open ? 'visible' : 'pointer-events-none invisible'}`}
        aria-hidden={!open}
      >
        {/* Scrim over the page */}
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setOpen(false)}
          className={`absolute inset-0 h-full w-full cursor-default bg-royal-900/50 backdrop-blur-[2px] transition-opacity duration-700 ${
            open ? 'opacity-100' : 'opacity-0'
          }`}
        />

        {/* Panel — a little over a quarter of a desktop screen, full width on phones */}
        <aside
          className={`absolute inset-y-0 right-0 flex w-full max-w-[430px] flex-col overflow-hidden bg-royal-900 shadow-luxe-lg transition-transform duration-[700ms] ease-luxe ${
            open ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="pointer-events-none absolute inset-0 paisley-bg opacity-[0.1]" />

          {/* Panel head */}
          <div className="relative flex h-[76px] shrink-0 items-center justify-between border-b border-cream/10 px-6 sm:px-8 lg:h-[96px]">
            <Link to="/" className="flex items-center gap-3">
              <img src={brand.markWhite} alt="" className="h-7 w-auto lg:h-8" />
              <span className="font-display text-[15px] leading-none tracking-[0.22em] text-cream lg:text-[17px]">
                PRAMUKH
              </span>
            </Link>

            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Close menu"
              className="flex items-center gap-2.5 text-[11px] font-medium uppercase tracking-luxe text-cream transition-colors hover:text-gold-200"
            >
              <span className="hidden sm:inline">Close</span>
              <IconClose className="h-4 w-4" />
            </button>
          </div>

          {/* Panel body */}
          <div className="relative min-h-0 flex-1 overflow-y-auto px-6 pb-10 pt-6 sm:px-8">
            <nav>
              {nav.map((item, i) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === '/'}
                  className={({ isActive }) =>
                    `group flex items-baseline gap-4 border-b border-cream/10 py-3.5 transition-colors duration-500 ${
                      isActive ? 'text-gold-200' : 'text-cream hover:text-gold-200'
                    }`
                  }
                  style={{
                    transitionDelay: open ? `${200 + i * 45}ms` : '0ms',
                    transform: open ? 'none' : 'translateX(20px)',
                    opacity: open ? 1 : 0,
                    transitionProperty: 'transform, opacity, color',
                    transitionDuration: '650ms',
                    transitionTimingFunction: 'cubic-bezier(0.16,1,0.3,1)',
                  }}
                >
                  <span className="w-6 shrink-0 text-[11px] tracking-wider2 text-gold">0{i + 1}</span>
                  <span className="font-display text-[1.75rem] leading-none">{item.label}</span>
                  <span className="ml-auto h-px w-0 self-center bg-gold transition-all duration-500 ease-luxe group-hover:w-8" />
                </NavLink>
              ))}
            </nav>

            <div className="mt-9">
              <p className="text-[11px] uppercase tracking-luxe text-gold-300">Collections</p>
              <div className="mt-4 grid grid-cols-2 gap-x-5 gap-y-2.5">
                {categories.map((c) => (
                  <Link
                    key={c.slug}
                    to={`/collections/${c.slug}`}
                    className="text-[13px] font-light text-cream/80 transition-colors duration-300 hover:text-gold-200"
                  >
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-9 border-t border-cream/10 pt-7">
              <p className="text-[11px] uppercase tracking-luxe text-gold-300">Office</p>
              <a
                href={`tel:${site.phone}`}
                className="mt-3 block font-display text-xl text-cream transition-colors hover:text-gold-200"
              >
                {site.phoneDisplay}
              </a>
              <a
                href={`mailto:${site.email}`}
                className="mt-1.5 block break-all text-[13px] font-light text-cream/75 transition-colors hover:text-gold-200"
              >
                {site.email}
              </a>

              <p className="mt-6 text-[11px] uppercase tracking-luxe text-gold-300">Showroom</p>
              <p className="mt-3 text-[13px] font-light leading-relaxed text-cream/75">
                {site.address.line1}, {site.address.line2},
                <br />
                {site.address.city} {site.address.pin}
              </p>
            </div>

            <div className="mt-8 flex items-center gap-3 border-t border-cream/10 pt-7">
              <a
                href={site.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="flex h-10 w-10 items-center justify-center border border-cream/15 text-cream/80 transition-all duration-400 hover:border-gold hover:text-gold"
              >
                <IconInstagram className="h-4 w-4" />
              </a>
              <a
                href={site.social.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="flex h-10 w-10 items-center justify-center border border-cream/15 text-cream/80 transition-all duration-400 hover:border-gold hover:text-gold"
              >
                <IconFacebook className="h-4 w-4" />
              </a>
              <Lotus className="ml-auto h-5 w-auto text-gold/60" />
            </div>
          </div>
        </aside>
      </div>
    </>
  );
}
